import { Button, Callout, Card, DropdownMenu, Heading, Icon, IconButton, Spinner, StatCard, StatCardGroup, Tag } from '@humanforce/mango-ui-react';
import type { PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunExceptionsPage, PayRunPopulationStatus } from './payRunPopulationApiTypes';
import { formatSystemValue } from './payRunsModel';

interface PayRunDetailHeaderProps {
  payRun: PayRunDetailDto;
  populationStatus: PayRunPopulationStatus | null;
  exceptions: PayRunExceptionsPage | null;
  onTransition: (verb: 'make_payroll_active' | 'revert_to_trial') => void;
  onLoadPopulation: () => void;
  transitioning: boolean;
  actionBusy: boolean;
  calculationBusy: boolean;
  calculationError: string | null;
  onCalculate: () => void;
  onRefreshCalculation: () => void;
  onEdit: () => void;
  onCancel: () => void;
}

const money = (value: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

const date = (value: string): string =>
  new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`));

const duration = (seconds: number): string => {
  const roundedSeconds = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(roundedSeconds / 60)}m ${roundedSeconds % 60}s`;
};

const completedCalculationMessage = (calculation: NonNullable<PayRunDetailDto['calculation']>): string => {
  const parts = [`${calculation.succeededWorkerCount} of ${calculation.workerCount} employees calculated successfully.`];
  const uncalculated = Math.max(
    0,
    calculation.workerCount - calculation.succeededWorkerCount
      - calculation.failedWorkerCount - calculation.reviewRequiredWorkerCount,
  );
  if (uncalculated > 0)
    parts.push(`${uncalculated} ${uncalculated === 1 ? 'employee was' : 'employees were'} not calculated. Review the Compliance tab.`);
  if (calculation.importDurationSeconds != null)
    parts.push(`Imported in ${duration(calculation.importDurationSeconds)}.`);
  if (calculation.startedAt && calculation.completedAt) {
    const calculationSeconds = (Date.parse(calculation.completedAt) - Date.parse(calculation.startedAt)) / 1000;
    if (Number.isFinite(calculationSeconds) && calculationSeconds >= 0)
      parts.push(`Calculated in ${duration(calculationSeconds)}.`);
  }
  return parts.join(' ');
};

const steps = [
  { value: 'open', label: 'Open', detail: 'Period' },
  { value: 'payroll_active', label: 'Compliance', detail: 'Pre-flight' },
  { value: 'ready_to_pay', label: 'Ready to Pay', detail: 'Approved' },
  { value: 'eft_ready', label: 'EFT Ready', detail: 'ABA + Super' },
  { value: 'complete', label: 'Complete', detail: 'STP + GL' },
];

const workflowTone = (status: string): 'positive' | 'neutral' | 'pending' => {
  if (status === 'ready_to_pay') return 'positive';
  if (status === 'cancelled' || status === 'complete') return 'neutral';
  return 'pending';
};

const summary = (
  payRun: PayRunDetailDto,
  status: PayRunPopulationStatus | null,
  exceptions: PayRunExceptionsPage | null,
) => {
  const calculated = payRun.calculation?.status === 'complete';
  return [
  { label: 'Employees', value: status?.employeeCount ?? payRun.employeeCount ?? 0, icon: 'person' as const },
  { label: 'Gross pay', value: calculated ? money(payRun.gross) : '—', icon: 'dollar-sign' as const },
  { label: 'Tax', value: calculated ? money(payRun.tax) : '—', icon: 'percent' as const },
  { label: 'Super', value: '—', icon: 'achievement' as const },
  { label: 'Net pay', value: calculated ? money(payRun.netPay) : '—', icon: 'ledger' as const },
  {
    label: 'Critical',
    value: exceptions?.summary.blocking ?? 0,
    icon: 'status-critical' as const,
    tone: 'red' as const,
  },
  {
    label: 'Warnings',
    value: (exceptions?.summary.warnings ?? 0) + (exceptions?.summary.reviewRequired ?? 0),
    icon: 'status-caution' as const,
    tone: 'yellow' as const,
  },
  ];
};

const calculationPresentation = (status: string | undefined) => {
  if (status === 'complete') return { label: 'Calculated', tone: 'positive' as const };
  if (status === 'failed') return { label: 'Calculation failed', tone: 'critical' as const };
  if (status === 'review_required') return { label: 'Review required', tone: 'caution' as const };
  if (status === 'admitted' || status === 'calculating') return { label: 'Calculating', tone: 'info' as const };
  if (status === 'ready') return { label: 'Ready to calculate', tone: 'neutral' as const };
  return { label: 'Inputs required', tone: 'neutral' as const };
};

export const PayRunDetailHeader = ({
  payRun,
  populationStatus,
  exceptions,
  onTransition,
  onLoadPopulation,
  transitioning,
  actionBusy,
  calculationBusy,
  calculationError,
  onCalculate,
  onRefreshCalculation,
  onEdit,
  onCancel,
}: PayRunDetailHeaderProps) => {
  const currentStep = steps.findIndex((step) => step.value === payRun.workflowStatus);
  const calculation = payRun.calculation;
  const calculationState = calculationPresentation(calculation?.status);
  const calculationRunning = calculation?.status === 'admitted' || calculation?.status === 'calculating';
  const ticketActions = [
    ...(payRun.meta.actions.includes('edit')
      ? [{ label: 'Edit', icon: 'edit' as const, onClick: onEdit }]
      : []),
    ...(payRun.meta.actions.includes('cancel')
      ? [{ label: 'Cancel', icon: 'delete' as const, variant: 'destructive' as const, onClick: onCancel }]
      : []),
  ];

  return (
    <div className="mgo-stack mgo-gap-l">
      <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-tablet-2 mgo-gap-l">
        <div className="mgo-stack mgo-gap-xs">
          <div className="mgo-cluster mgo-gap-s">
            <Heading level={1}>{payRun.displayTitle}</Heading>
            <Tag
              label={formatSystemValue(payRun.workflowStatus)}
              tone={workflowTone(payRun.workflowStatus)}
            />
          </div>
          <p className="mgo-text-s mgo-color-muted">
            {payRun.businessRecordName} · Pay period {date(payRun.periodStartDate)}–{date(payRun.periodEndDate)}
            {' '}· Payment date <strong>{date(payRun.paymentDate)}</strong>
          </p>
        </div>
        <div className="mgo-cluster mgo-gap-s mgo-justify-end">
          {payRun.meta.actions.includes('load_population_sources') && (
            <Button
              label="Load Pay Run data"
              variant="secondary"
              icon="upload"
              disabled={transitioning || actionBusy}
              onClick={onLoadPopulation}
            />
          )}
          {payRun.meta.actions.includes('make_payroll_active') && (
            <Button
              label="Make Payroll Active"
              icon="status-positive"
              loading={transitioning}
              disabled={actionBusy}
              onClick={() => onTransition('make_payroll_active')}
            />
          )}
          {payRun.meta.actions.includes('revert_to_trial') && (
            <Button
              label="Revert to Trial"
              variant="secondary"
              icon="refresh"
              loading={transitioning}
              disabled={actionBusy}
              onClick={() => onTransition('revert_to_trial')}
            />
          )}
          {(payRun.meta.actions.includes('calculate') || payRun.meta.actions.includes('retry_calculation')) && (
            <Button
              label={payRun.meta.actions.includes('retry_calculation') ? 'Retry calculation' : 'Calculate Pay Run'}
              icon="payroll-dollar"
              loading={calculationBusy || calculationRunning}
              disabled={transitioning || actionBusy || calculationRunning}
              onClick={onCalculate}
            />
          )}
          {ticketActions.length > 0 && (
            <DropdownMenu
              testId="pay-run-actions"
              trigger={<IconButton icon="more" label="Pay Run actions" variant="tertiary" disabled={transitioning || actionBusy} />}
              items={ticketActions}
            />
          )}
          <Button
            label="Pay employees"
            icon="payroll-dollar"
            disabled
            tooltip="Payment is delivered by a later Pay Run phase."
          />
        </div>
      </div>

      {calculationError && (
        <Callout
          tone="critical"
          title={calculationRunning ? 'Calculation status unavailable' : 'Calculation not started'}
          action={calculationRunning
            ? <Button label="Retry status" size="small" variant="secondary" loading={calculationBusy} onClick={onRefreshCalculation} />
            : undefined}
        >
          {calculationError}
        </Callout>
      )}
      <Callout
        tone={calculationState.tone}
        title={calculationState.label}
      >
        {calculationRunning ? (
          <span className="mgo-cluster mgo-gap-s"><Spinner /> Employee and transaction inputs are being calculated.</span>
        ) : calculation?.status === 'complete' ? (
          completedCalculationMessage(calculation)
        ) : calculation?.status === 'failed' || calculation?.status === 'review_required' ? (
          calculation.issues[0]?.detail ?? 'Review the calculation issues, then retry this pinned attempt.'
        ) : calculation?.status === 'ready' ? (
          'Employee and transaction inputs are loaded and ready.'
        ) : (
          'Load complete employee and transaction inputs before calculating.'
        )}
      </Callout>
      {calculation && ['failed', 'review_required'].includes(calculation.status) && calculation.issues.length > 0 && (
        <Card>
          <div className="mgo-stack mgo-gap-s">
            <Heading level={2}>Calculation issues</Heading>
            <ul className="mgo-stack mgo-gap-xs" style={{ margin: 0 }}>
              {calculation.issues.map((issue, index) => (
                <li key={`${issue.code}-${issue.workerId ?? 'run'}-${index}`}>
                  <span className="mgo-weight-strong">{formatSystemValue(issue.code)}</span>
                  {issue.workerId ? ` · Employee ${issue.workerId}` : ''}: {issue.detail}
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      <Card>
        <div style={{ overflowX: 'auto' }}>
          <ol
            className="mgo-grid mgo-grid-cols-5 mgo-gap-s"
            aria-label="Pay Run progress"
            style={{ listStyle: 'none', margin: 0, minWidth: '42rem', padding: 0 }}
          >
          {steps.map((step, index) => {
            const complete = index < currentStep;
            const active = currentStep >= 0 && index === currentStep;
            return (
              <li
                key={step.value}
                className="mgo-cluster mgo-gap-s"
                aria-current={active ? 'step' : undefined}
                style={{
                  color: active
                    ? 'var(--mgo-color-fg-info)'
                    : complete
                      ? 'var(--mgo-color-fg-positive)'
                      : 'var(--mgo-color-fg-muted)',
                }}
              >
                <span className="mgo-text-l">
                  <Icon name={complete ? 'status-positive' : active ? 'radio' : 'status-no-action'} />
                </span>
                <span className="mgo-stack mgo-gap-2xs">
                  <span className="mgo-text-s mgo-weight-strong">{step.label}</span>
                  <span className="mgo-text-xs mgo-color-muted">{step.detail}</span>
                </span>
              </li>
            );
          })}
          </ol>
        </div>
      </Card>

      <section aria-label="Pay Run summary">
        <StatCardGroup>
        {summary(payRun, populationStatus, exceptions).map((item) => (
          <StatCard key={item.label} label={item.label} value={item.value} icon={item.icon} tone={item.tone} />
        ))}
        </StatCardGroup>
      </section>
    </div>
  );
};
